<template>
	<v-app-bar absolute color="primary" elevation="0" class="text-center px-2 py-0 trial-top-bar">
		<span class="d-flex ga-2 mx-auto text-white text-uppercase font-weight-bold align-center justify-center trial-top-bar-content">
			<v-icon class="d-flex" color="white">icon-info-circle-stroke</v-icon>

			<!-- Orgs with new subscriptions -->
			<div v-if="subscription && isNewSubscription" class="d-flex">
				<!-- Banner for admins -->
				<div v-if="canUpdateOrg">
					<span v-if="showPendingPaymentGracePeriodMessage && hasPendingPaymentGraceEndDate">
						It looks like your recent LiveSwitch payment didn't go through. No worries — you have until
						{{ paymentPendingGraceEndDate }} to update your
						<a class="text-white text-decoration-underline clickable-link" @click="$emit('upgrade')">payment method</a> and
						avoid any service interruptions.
					</span>
					<span v-else-if="showPendingPaymentGracePeriodMessage">
						It looks like your recent LiveSwitch payment didn't go through. No worries —
						<a class="text-white text-decoration-underline clickable-link" @click="$emit('upgrade')"
							>update your payment method</a
						>
						to avoid any service interruptions.
					</span>
					<span v-else-if="trialExpired && !subscription.hasPaymentMethod"
						>Your free trial has ended.
						<a class="text-white text-decoration-underline clickable-link" @click="$emit('upgrade')">Select your plan</a>
						to continue using premium features.
					</span>
					<span v-else-if="trialExpired"
						>Your free trial has ended. Your account needs attention. Please access your subscription info in the
						<a class="text-white text-decoration-underline clickable-link" @click="$emit('upgrade')"
							>customer billing portal.</a
						>
					</span>
					<span v-else-if="effectiveTrialDaysRemaining > 0 && !subscription.hasPaymentMethod"
						>{{ trialDays }} remaining in your free trial.
						<a class="text-white text-decoration-underline clickable-link" @click="$emit('upgrade')">Select your plan</a>
						to continue using premium features once your trial has ended.
					</span>
					<span v-else-if="effectiveTrialDaysRemaining > 0 && subscription.hasPaymentMethod"
						>{{ trialDays }} remaining in your free trial. At the end of your trial, we will automatically charge the payment
						method on file.
					</span>
					<span v-else-if="isTrialing && !subscription.hasPaymentMethod"
						>You're on a free trial.
						<a class="text-white text-decoration-underline clickable-link" @click="$emit('upgrade')">Select your plan</a>
						to continue using premium features once your trial has ended.
					</span>
					<span v-else-if="isTrialing"
						>You're on a free trial. At the end of your trial, we will automatically charge the payment method on file.
					</span>
				</div>

				<!-- Banner for non-admins -->
				<div v-else>
					<span v-if="showPendingPaymentGracePeriodMessage && hasPendingPaymentGraceEndDate">
						It looks like your recent LiveSwitch payment didn't go through. No worries — you have until
						{{ paymentPendingGraceEndDate }} to contact an administrator to update your payment method and avoid any service
						interruptions.
					</span>
					<span v-else-if="showPendingPaymentGracePeriodMessage">
						It looks like your recent LiveSwitch payment didn't go through. No worries — contact an administrator to update your
						payment method and avoid any service interruptions.
					</span>
					<span v-else-if="trialExpired && !subscription.hasPaymentMethod"
						>Your free trial has ended. Contact an administrator to add a payment method to continue using premium features.
					</span>
					<span v-else-if="trialExpired"
						>Your free trial has ended. Your account needs attention. Contact an Administrator.
					</span>
					<span v-else-if="effectiveTrialDaysRemaining > 0 && !subscription.hasPaymentMethod"
						>{{ trialDays }} remaining in your free trial. Contact an administrator to add a payment method to continue using
						premium features once your trial has ended.
					</span>
					<span v-else-if="effectiveTrialDaysRemaining > 0 && subscription.hasPaymentMethod"
						>{{ trialDays }} remaining in your free trial. At the end of your trial, we will automatically charge the payment
						method on file.
					</span>
					<span v-else-if="isTrialing"
						>You're on a free trial. Contact an administrator to manage your subscription before your trial ends.
					</span>
				</div>
			</div>

			<!-- Legacy orgs without subscription or legacy subscriptions -->
			<div v-else class="d-flex font-weight-bold">
				<!-- Banner for all users -->
				<span v-if="isEndedTrial"
					>Contact <a class="text-white text-decoration-underline" :href="`mailto:${helpEmail}`">{{ helpEmail }}</a> to upgrade
					your subscription.
				</span>
				<span v-else-if="isTrialCCNow">
					Welcome! Contact <a class="text-white text-decoration-underline" :href="`mailto:${helpEmail}`">{{ helpEmail }}</a> to
					select your plan or to start your free trial.
				</span>
				<span v-else-if="trialDaysRemaining > -1">
					{{ trialDaysRemaining > 1 ? `${trialDaysRemaining} Days` : "1 Day" }} remaining on your free trial. Ready for the full
					experience? Contact <a class="text-white text-decoration-underline" :href="`mailto:${helpEmail}`">{{ helpEmail }}</a> to
					upgrade your subscription.
				</span>
			</div>
		</span>
	</v-app-bar>
</template>
<script>
	import Account from "../api/account";
	import api from "@/api";
	import constants from "@/api/constants";
	import { isNewSubscription, effectiveTrialDaysRemaining } from "../helpers/subscription";

	export default {
		name: "TrialTopBar",
		props: {
			isEndedTrial: Boolean,
			isTrialCCNow: Boolean,
			trialDaysRemaining: Number
		},
		data() {
			return {
				canUpdateOrg: false,
				subscription: null,
				isNewSubscription: false,
				helpEmail: window.env.VITE_SUPPORT_EMAIL || "help@liveswitch.com"
			};
		},
		computed: {
			// Prefer the subscription's own trial countdown for new subscriptions — the org-derived
			// trialDaysRemaining prop can be stale/negative when a trial was extended in Stripe.
			effectiveTrialDaysRemaining() {
				return effectiveTrialDaysRemaining(this.subscription, this.trialDaysRemaining);
			},
			isTrialing() {
				return this.subscription?.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL;
			},
			trialDays() {
				if (this.effectiveTrialDaysRemaining != 1) {
					let days = this.effectiveTrialDaysRemaining;

					if (days < 0) {
						days = 0;
					}

					return `${days} days`;
				} else {
					return "1 day";
				}
			},
			trialExpired() {
				return this.subscription?.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL_EXPIRED;
			},
			showPendingPaymentGracePeriodMessage() {
				return this.subscription?.isInPendingPaymentGracePeriod;
			},
			hasPendingPaymentGraceEndDate() {
				if (!this.subscription?.pendingPaymentGraceEndDate) {
					return false;
				}

				return true;
			},
			paymentPendingGraceEndDate() {
				if (!this.subscription?.pendingPaymentGraceEndDate) {
					return "";
				}

				return new Date(this.subscription.pendingPaymentGraceEndDate).toLocaleDateString("en-US");
			}
		},
		async mounted() {
			const me = await api.getMe();
			const account = new Account(me.account);
			this.canUpdateOrg = account.hasPermissionTo("UPDATE_ORGANIZATION");
		},
		methods: {
			setSubscriptionInfo(subscription) {
				this.subscription = subscription;
				this.isNewSubscription = isNewSubscription(subscription);

				// Diagnostic: a trialing subscription with no usable day count means the banner fell back
				// to the generic message — surface the state so a future recurrence is triagable.
				if (this.isTrialing && this.effectiveTrialDaysRemaining <= 0) {
					console.warn("TrialTopBar: trialing subscription has no positive days remaining; showing fallback banner.", {
						subscriptionId: subscription?.id,
						accountStatus: subscription?.accountStatus,
						orgTrialDaysRemaining: this.trialDaysRemaining,
						subscriptionTrialDaysRemaining: subscription?.trialDaysRemaining,
						hasPaymentMethod: subscription?.hasPaymentMethod,
						isNewSubscription: this.isNewSubscription
					});
				}
			}
		}
	};
</script>
<style lang="scss" scoped>
	.clickable-link {
		cursor: pointer;
	}
	.trial-top-bar {
		height: auto !important;
		min-height: 52px;
	}
	.trial-top-bar :deep(.v-toolbar__content) {
		height: auto !important;
		min-height: 52px !important;
	}
	.trial-top-bar-content {
		flex-wrap: wrap;
	}
	@media #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: portrait) {
		.trial-top-bar {
			font-size: 1.25vh;
		}
	}
	@media #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: landscape) {
		.trial-top-bar {
			font-size: 1.25vw;
		}
	}
</style>
